'use client'

import { Card } from '@vinted/web-ui'
import { useEffect } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useSession } from '@marketplace-web/shared/session-data'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'

import {
  getItemBumpStatsSvcVas,
  useGetItemBumpStatsSvcVasApiGateway,
  getItemPerformance,
  transformItemBumpStatsResponse,
  transformItemPerformanceResponse,
} from '@marketplace-web/vas/vas-data'

import ItemPerformance from './ItemPerformance'
import ItemPerformanceEmptyState from './ItemPerformance/ItemPerformanceEmptyState'
import { ItemPagePerformanceBlockModel } from './types'

type Props = {
  performanceBlockPluginModel: ItemPagePerformanceBlockModel | undefined
}

const ItemPagePerformanceBlockPlugin = ({ performanceBlockPluginModel }: Props) => {
  const getItemBumpStatsSvcVasApiGateway = useGetItemBumpStatsSvcVasApiGateway()
  const { user: currentUser } = useSession()
  const trackExpose = useTrackAbTestCallback()
  const vasApiGatewaySwapBumpStatsAbTest = useAbTest('vas_api_gateway_swap_bump_stats_v2')
  const isVasApiGatewaySwapBumpStatsAbTestOn = vasApiGatewaySwapBumpStatsAbTest?.variant === 'on'

  const isApiGatewayClientEnabled = useFeatureSwitch('web_api_gateway_client')
  const isApiGatewayClientEnabledForVas = useFeatureSwitch('web_api_gateway_client_vas')
  const isApiGatewayEnabled = isApiGatewayClientEnabled && isApiGatewayClientEnabledForVas

  const getItemBumpStatsSvcVasRequest = isApiGatewayEnabled
    ? getItemBumpStatsSvcVasApiGateway
    : getItemBumpStatsSvcVas

  const {
    fetch: fetchItemPerformanceCore,
    transformedData: performanceCore,
    isLoading: isPerformanceLoadingCore,
  } = useFetch(getItemPerformance, transformItemPerformanceResponse)

  const {
    fetch: fetchItemPerformanceSvcVas,
    transformedData: performanceSvcVas,
    isLoading: isPerformanceLoadingSvcVas,
  } = useFetch(getItemBumpStatsSvcVasRequest, transformItemBumpStatsResponse)

  const fetchItemPerformance = isVasApiGatewaySwapBumpStatsAbTestOn
    ? fetchItemPerformanceSvcVas
    : fetchItemPerformanceCore
  const performance = isVasApiGatewaySwapBumpStatsAbTestOn ? performanceSvcVas : performanceCore
  const isPerformanceLoading = isVasApiGatewaySwapBumpStatsAbTestOn
    ? isPerformanceLoadingSvcVas
    : isPerformanceLoadingCore

  const itemId = performanceBlockPluginModel?.itemId
  const userId = performanceBlockPluginModel?.userId
  const isViewingSelf = userId && currentUser && currentUser.id === userId
  const areStatsVisible = performanceBlockPluginModel?.areStatsVisible

  useEffect(() => {
    if (!userId || !itemId || !isViewingSelf || !areStatsVisible) return

    fetchItemPerformance(itemId)
    trackExpose(vasApiGatewaySwapBumpStatsAbTest)
  }, [
    itemId,
    userId,
    isViewingSelf,
    areStatsVisible,
    fetchItemPerformance,
    trackExpose,
    vasApiGatewaySwapBumpStatsAbTest,
  ])

  if (!isViewingSelf || !areStatsVisible) return null

  if (!performance || isPerformanceLoading) return <ItemPerformanceEmptyState />

  const { isBumped } = performanceBlockPluginModel

  return (
    <Card>
      <div className="u-overflow-hidden">
        <ItemPerformance performance={performance} itemPromoted={isBumped} />
      </div>
    </Card>
  )
}

export default ItemPagePerformanceBlockPlugin
